import { useCallback, useEffect, useRef, useState } from 'react';
import {
  nextRetryPhase,
  resolveListLoadAnnouncement,
  resolveListLoadPanels,
  resolveListLoadView,
  type RetryPhase,
} from '../utils/list-load-view';

/**
 * The page-side half of a retryable list load (PSTUDIO-4177 for Child orgs,
 * PSTUDIO-4442 for Users and Teams): which panel to show, what the live region
 * says, the retry handler, and the focus hand-back after a successful retry.
 *
 * It takes the two machine facts as booleans and the load event as a callback,
 * so it imports nothing from the machine or the design system and renders in
 * jsdom.
 *
 * Kept here rather than in each page: the phase bookkeeping below was written
 * once, for Child orgs, after review found two defects in the first version of
 * it (see `nextRetryPhase`). A second copy would have to rediscover both.
 */
export const useRetryableListLoad = ({
  loaded,
  failed,
  requestLoad,
}: {
  loaded: boolean;
  failed: boolean;
  /** Sends the region's load event. Called from the retry button only. */
  requestLoad: () => void;
}) => {
  const view = resolveListLoadView({ loaded, failed });

  // PSTUDIO-4177 (WCAG 2.4.3). A retry moves the machine error -> loading, and
  // if the error panel were simply swapped for the loader the button the user
  // just pressed would unmount under their focus, dropping it to <body>. Keeping
  // the panel mounted for the duration of the retry — with the button in its
  // busy state — leaves focus where it was.
  //
  // The phase is written by the CLICK, never derived from a rendered frame: a
  // retry whose `loading` frame React batches away still has to announce itself.
  const [retryPhase, setRetryPhase] = useState<RetryPhase>('idle');

  const retry = useCallback(() => {
    setRetryPhase('requested');
    requestLoad();
  }, [requestLoad]);

  useEffect(() => {
    setRetryPhase((phase) => nextRetryPhase({ view, phase }));
  }, [view]);

  const { showLoader, showError, busyRetry } = resolveListLoadPanels({
    view,
    retrying: retryPhase === 'requested',
  });

  const announcement = resolveListLoadAnnouncement({
    view,
    busyRetry,
    recovered: retryPhase === 'recovered',
  });

  // PSTUDIO-4177 (WCAG 2.4.3), the other half of keeping focus: a SUCCESSFUL
  // retry unmounts the panel the button lived in, so focus falls to <body> and
  // the next Tab restarts from the top of the document. Move it to the section
  // the user was waiting for — but only if it really was dropped, so this can
  // never steal focus from somewhere the user has since moved to.
  const recoveredSectionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (retryPhase !== 'recovered' || view !== 'ready') return;

    if (document.activeElement === document.body) {
      recoveredSectionRef.current?.focus();
    }
  }, [retryPhase, view]);

  return {
    view,
    showLoader,
    showError,
    busyRetry,
    announcement,
    retry,
    recoveredSectionRef,
  };
};
